import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Inbox, ArrowUpRight } from "lucide-react";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";
import InboxRow from "@/components/inbox/InboxRow";
import InboxXpBar from "@/components/inbox/InboxXpBar";

/**
 * Transaction inbox — shows Plaid-imported expenses the user hasn't
 * confirmed yet. Each row lets you (a) accept as-is, (b) change category
 * before accepting, or (c) delete outright. Reviewed rows drop out of
 * the list. Renders nothing when the inbox is empty.
 */
export default function TransactionsInbox({ currency }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [xp, setXp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // Persist last-badge date so we only celebrate once per week
  const [justEarned, setJustEarned] = useState(false);

  const load = useCallback(async () => {
    try {
      const [inbox, x] = await Promise.all([
        api.get("/expenses/inbox"),
        api.get("/expenses/inbox/xp"),
      ]);
      setItems(inbox.data?.items || []);
      setXp(x.data || null);
    } catch {
      // silent — the card just doesn't render on load failure
      setItems([]);
      setXp(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const refreshXp = useCallback(async (prevXp) => {
    try {
      const { data } = await api.get("/expenses/inbox/xp");
      // Detect the moment badge is earned (crossing the target)
      if (prevXp && !prevXp.badge_earned && data.badge_earned) {
        // Only celebrate once per calendar day to avoid toast spam
        try {
          const key = "pfo_inbox_hero_celebrated_on";
          const today = new Date().toISOString().slice(0, 10);
          const prev = localStorage.getItem(key);
          if (prev !== today) {
            localStorage.setItem(key, today);
            setJustEarned(true);
            toast.success(t("inbox.toast_hero_title"), {
              description: t("inbox.toast_hero_desc"),
              duration: 5000,
            });
          }
        } catch { /* localStorage may be unavailable */ }
      }
      setXp(data);
    } catch { /* silent */ }
  }, [t]);

  const approve = async (row, categoryOverride) => {
    setBusyId(row.id);
    try {
      const body = {};
      if (categoryOverride && categoryOverride !== row.category) {
        body.category = categoryOverride;
      }
      await api.post(`/expenses/${row.id}/review`, body);
      setItems(prev => prev.filter(r => r.id !== row.id));
      toast.success(t("inbox.toast_approved"));
      refreshXp(xp);
    } catch (e) {
      console.error("Approve failed:", e);
      toast.error(t("inbox.toast_approve_failed"));
    } finally { setBusyId(null); }
  };

  const trash = async (row) => {
    if (!confirm(t("inbox.confirm_delete", { desc: row.description, amount: formatMoney(row.amount, currency) }))) return;
    setBusyId(row.id);
    try {
      await api.delete(`/expenses/${row.id}`);
      setItems(prev => prev.filter(r => r.id !== row.id));
      toast.success(t("common.toast_deleted"));
    } catch (e) {
      console.error("Delete failed:", e);
      toast.error(t("inbox.toast_delete_failed"));
    } finally { setBusyId(null); }
  };

  if (loading || items.length === 0) return null;

  const shown = items.slice(0, 5);
  const remaining = items.length - shown.length;

  return (
    <div
      className="mb-6 p-5 rounded-2xl border shadow-soft"
      style={{
        borderColor: "rgba(224,158,80,0.30)",
        background: "linear-gradient(135deg, rgba(224,158,80,0.08) 0%, rgba(45,90,64,0.06) 55%, rgba(224,158,80,0.05) 100%)",
      }}
      data-testid="transactions-inbox-card"
    >
      <div className="flex items-start gap-4">
        <div
          className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: "linear-gradient(155deg, #E7B77C 0%, #A16A25 100%)", color: "#FDFCFB" }}
        >
          <Inbox size={19} strokeWidth={2.1} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
            <div className="label-eyebrow" style={{ color: "#A16A25" }}>
              {t("inbox.eyebrow")}
            </div>
            <Link
              to="/connections"
              className="text-xs font-semibold text-[var(--accent-primary)] hover:underline inline-flex items-center gap-1"
              data-testid="inbox-connections-link"
            >
              {t("inbox.bank_connections_cta")}
            </Link>
          </div>
          <div
            className="text-base font-medium leading-snug tracking-tight"
            style={{ fontFamily: "var(--font-heading)" }}
            data-testid="inbox-headline"
          >
            {items.length === 1
              ? t("inbox.headline_one")
              : t("inbox.headline_other", { count: items.length })}
          </div>

          <InboxXpBar xp={xp} justEarned={justEarned} t={t} />

          <ul
            className="mt-4 divide-y divide-[var(--border-default)]"
            data-testid="inbox-list"
          >
            {shown.map((row) => (
              <InboxRow
                key={row.id}
                row={row}
                currency={currency}
                busy={busyId === row.id}
                onApprove={approve}
                onDelete={trash}
                t={t}
              />
            ))}
          </ul>

          {remaining > 0 && (
            <div
              className="mt-3 text-xs text-[var(--text-muted)]"
              data-testid="inbox-remaining"
            >
              {remaining === 1 ? t("inbox.remaining_one") : t("inbox.remaining_other", { count: remaining })}
              <Link
                to="/expenses"
                className="font-semibold text-[var(--accent-primary)] hover:underline inline-flex items-center gap-0.5"
              >
                {t("inbox.expenses_link")} <ArrowUpRight size={11}/>
              </Link>.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
